import React, { useContext, useState, useEffect, useRef, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Typography,
  ListItemText,
  Avatar,
  Button,
  Box,
  IconButton,
  Chip,
  useMediaQuery,
  Dialog,
  Modal,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { ThemeContext } from '../../../lib/contexts/ThemeContext';
import MealPreview from './MealPreview';
import NutritionCardPreview from './NutritionCardPreview';
import OrderDetailsAdjustmentCard from './OrderDetailsAdjustmentCard';
import Drawer from '@mui/material/Drawer';
import { OrdersContext } from '../../../lib/contexts/OrdersContext';
import { themeContext } from '../../../lib/contexts/ThemeContext';
import MealInfoCard from './MealInfoCard';
import ReportOrderIssueCard from './subComponents/ReportOrderIssueCard';
import { BenefitDetailsContext } from '../../../lib/contexts/BenefitDetailsContext';
import { findBenefitByID } from '../../../lib/contexts/BenefitDetailsContext';

// Utility function to convert hex to RGB
const hexToRgb = (hex) => {
  hex = hex.replace(/^#/, '');
  let bigint = parseInt(hex, 16);
  let r = (bigint >> 16) & 255;
  let g = (bigint >> 8) & 255;
  let b = bigint & 255;
  return `${r}, ${g}, ${b}`;
};

// Utility function to calculate luminance
const calculateLuminance = (r, g, b) => {
  const a = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
};

// Utility function to determine the best contrasting text color
const getContrastingTextColor = (hex) => {
  const rgb = hexToRgb(hex).split(', ').map(Number);
  const luminance = calculateLuminance(rgb[0], rgb[1], rgb[2]);
  return luminance > 0.5 ? '#000' : '#fff';
};

const formatDate = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatStatus = (status) => {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const reportIssueButton = () => {
  return (
    <Button
      id="feedback-button"
      className="feedback-button"
      sx={{
        display: 'flex',
        width: '155px',
        height: 'var(--3, 36px)',
        padding: 'var(--0, 0px) var(--3, 13px)',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 'var(--3, 13px)',
        borderRadius: 'var(--3, 7px)',
        border: 'var(--1, 2px) solid var(--color12, #1C124D)',
      }}
      // onClick={toggleReportIssueCard}
    >
      <Typography
        sx={{
          color: 'var(--color12, #1C124D)',
          textAlign: 'center',
          leadingTrim: 'both',
          textEdge: 'cap',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 700,
          lineHeight: 'normal',
        }}
      >
        Report Issue
      </Typography>
    </Button>
  );
};

const OrderDetails = forwardRef(
  ({ order, allowShowReportIssueButton = false, showPopUps, onMealsChange, showFull = false, from }, ref) => {
    const navigate = useNavigate();
    const {
      state: { finalTheme },
    } = useContext(ThemeContext);
    const {
      state: { ordersData, ordersMetrics },
      actions: { adjustTag },
    } = useContext(OrdersContext);
    const [hoveredMealId, setHoveredMealId] = useState(null);
    const [previewPosition, setPreviewPosition] = useState({ top: 0, left: 0 });
    const [showNutritionCard, setShowNutritionCard] = useState(false);
    const [isAdjustmentCardOpen, setIsAdjustmentCardOpen] = useState(false);
    const [currentMeals, setCurrentMeals] = useState(order.order_items || []);
    const hideTimeoutRef = useRef(null);
    const {
      state: { darkMode },
    } = useContext(ThemeContext);
    const isMobileView = useMediaQuery('(max-width: 600px)');
    // const [isExpanded, setIsExpanded] = useState(false);
    const [showAllMeals, setShowAllMeals] = useState(false);
    const [selectedMeal, setSelectedMeal] = useState(null);
    const [isReportIssueOpen, setIsReportIssueOpen] = useState(false);
    const { benefitDetails } = useContext(BenefitDetailsContext);
    const [maxQuantity, setMaxQuantity] = useState(14);
    const [showNumberOfItems, setShowNumberOfItems] = useState(false);

    useEffect(() => {
      const chosenBenefit = findBenefitByID(benefitDetails, order.benefit_id);
      if (chosenBenefit) {
        setMaxQuantity(chosenBenefit?.no_of_meals);
      }
    }, [benefitDetails]);
    useEffect(() => {
      if (ordersData) {
        const updatedOrder = ordersData.find((o) => o.id === order.id);
        if (updatedOrder && updatedOrder.order_items !== currentMeals) {
          setCurrentMeals(updatedOrder.order_items || []);
        }
      }
    }, [ordersData, order.id, currentMeals]);

    useEffect(() => {
      const timer = setTimeout(() => {
        if (!ordersMetrics || ordersMetrics[order.id]?.totalServingsNeeded === undefined) {
          setShowNumberOfItems(true);
        }
      }, 8000);

      return () => clearTimeout(timer);
    }, [ordersMetrics, order.id]);

    const numberOfItems = () => {
      let totalItems = 0;
      for (let item of order.order_items) {
        totalItems += item.quantity;
      }
      return totalItems;
    };

    const getHeader = (order) => {
      let displayedInformation;
      let messageLabel;
      if (order.dropoff_time != null) {
        displayedInformation = formatDate(order.dropoff_time);
        messageLabel = 'Delivered:';
      } else if (order.cutoff_time != null) {
        displayedInformation = formatDate(order.cutoff_time);
        messageLabel = 'Edit Window:';
      } else if (order.placed_at != null) {
        displayedInformation = formatDate(order.placed_at);
        messageLabel = 'Placed At:';
      }
      const today = new Date();
      const deliveryDate = new Date(order.dropoff_time);
      const oneWeekFromToday = new Date();
      oneWeekFromToday.setDate(today.getDate() + 7);

      let displayText;
      let typographyStyles;

      if (order.dropoff_time != null) {
        if (
          deliveryDate.getTime() > today.getTime() &&
          order?.cutoff_time &&
          new Date(order.cutoff_time).getTime() > today.getTime()
        ) {
          displayText = formatDate(order.dropoff_time);
          typographyStyles = {
            color: 'var(--color12, #1C124D)',
            leadingTrim: 'both',
            textEdge: 'cap',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: 'normal',
          };
        } else if (deliveryDate.getTime() <= oneWeekFromToday.getTime()) {
          displayText = 'Closed';
          typographyStyles = {
            color: 'var(--Gray-gray11, #6F6F6F)',
            leadingTrim: 'both',
            textEdge: 'cap',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: 'normal',
          };
        } else {
          const daysLeft = Math.floor((deliveryDate - oneWeekFromToday) / (1000 * 60 * 60 * 24));
          displayText = `${daysLeft} days left`;
          typographyStyles = {
            color: 'var(--secondary-mint-accessilbe, #1D7E6B)',
            leadingTrim: 'both',
            textEdge: 'cap',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: 'normal',
          };
        }
      } else if (order.cutoff_time != null) {
        displayText = formatDate(order.cutoff_time);
        messageLabel = 'Editable Until:';
      } else if (order.placed_at != null) {
        displayText = formatDate(order.placed_at);
        messageLabel = 'Placed At:';
      }

      return (
        <Box
          id="delivery-date-container"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            alignSelf: 'stretch',
          }}
        >
          <Box
            id="delivery-date-content"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 'var(--3, 7px)',
            }}
          >
            <Typography
              sx={{
                color: 'var(--Gray-gray12, #171717)',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
              }}
            >
              {order.dropoff_time != null
                ? deliveryDate.getTime() > today.getTime()
                  ? 'Edit Window:'
                  : 'Delivered:'
                : messageLabel}
            </Typography>
            <Typography
              sx={{
                color: messageLabel !== 'Delivered:' ? 'var(--secondary-mint-accessilbe, #1D7E6B)' : 'grey',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              {displayText}
            </Typography>
          </Box>
          {order &&
          (adjustTag(order) === 'DELIVERY FAILED' || adjustTag(order) === 'DELIVERED') &&
          allowShowReportIssueButton ? (
            reportIssueButton()
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                alignSelf: 'stretch',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',
                }}
              >
                Delivery Date:
              </Typography>
              <Typography
                sx={{
                  color: 'var(--color12, #1C124D)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                {order.dropoff_time ? formatDate(order.dropoff_time) : 'Pending'}
              </Typography>
            </Box>
          )}
        </Box>
      );
    };

    const handleShowOrderDetails = () => {
      setIsAdjustmentCardOpen(true);
    };

    const handleCloseAdjustmentCard = () => {
      setIsAdjustmentCardOpen(false);
    };

    const handleNavigateToOrderDetails = () => {
      navigate(`/orders/${order.id}`, { state: { from } });
    };

    const getStatusColor = (status) => {
      if (!status) return 'default';
      switch (status.toLowerCase()) {
        case 'delivered':
          return 'success';
        case 'canceled':
          return 'error';
        case 'scheduled':
          return 'info';
        case 'shipped':
          return 'success';
        case 'pending':
          return 'warning';
        default:
          return 'default';
      }
    };

    const handleMouseEnter = (mealId, event) => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      setHoveredMealId(mealId);
      const mouseX = event.clientX;
      const mouseY = event.clientY;
      setPreviewPosition({
        top: mouseY + window.scrollY,
        left: mouseX + 20,
      });
      setShowNutritionCard(true);
    };

    const handleMouseLeave = () => {
      hideTimeoutRef.current = setTimeout(() => {
        setHoveredMealId(null);
        setShowNutritionCard(false);
      }, 200);
    };

    const headerBackgroundColor = finalTheme
      ? `rgba(${hexToRgb(finalTheme.palette.primary.main)}, 0.9)`
      : darkMode
      ? '#444'
      : 'grey';
    const headerTextColor = finalTheme
      ? getContrastingTextColor(finalTheme.palette.primary.main)
      : darkMode
      ? '#ddd'
      : 'grey';

    const handleViewMoreClick = () => {
      setShowAllMeals(!showAllMeals);
    };

    const handleMealClick = (meal) => {
      setSelectedMeal(meal);
    };

    const closeMealInfoCard = () => {
      setSelectedMeal(null);
    };

    const toggleReportIssueCard = () => {
      setIsReportIssueOpen((prev) => !prev);
    };

    // Return the component JSX wrapped in a div with the ref
    return (
      <div ref={ref}>
        <Box
          id="order-details-container-main"
          sx={{
            display: 'flex',
            width: isMobileView ? '327px' : showFull ? '422px' : '345px',
            paddingBottom: 'var(--5, 24px)',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            flexShrink: 0,
            borderRadius: '8px',
            border: '2px solid var(--color3, #F5F4F9)',
            background: 'var(--Gray-gray2, #F8F8F8)',
          }}
        >
          {!showFull && (
            <Box
              id="order-header-container"
              onClick={handleNavigateToOrderDetails}
              sx={{
                display: 'flex',
                padding: '12px 16px',
                justifyContent: 'space-between',
                alignItems: 'center',
                alignSelf: 'stretch',
                borderRadius: '8px 8px 0px 0px',
                cursor: 'pointer',
                background:
                  order &&
                  (adjustTag(order) === 'CONFIRMED' ||
                    adjustTag(order) === 'PICKED UP' ||
                    adjustTag(order) === 'APPROVED')
                    ? '#D6D2E7'
                    : order &&
                      (adjustTag(order) === 'FUTURE' ||
                        adjustTag(order) === 'UP NEXT' ||
                        adjustTag(order) === 'NO HUB' ||
                        adjustTag(order) === 'ASSIGNED' ||
                        adjustTag(order) === 'PLACED')
                    ? '#1C124D'
                    : 'var(--Gray-gray5, #E8E8E8)',
              }}
            >
              <Box
                id="order-header-title"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--3, 13px)',
                }}
              >
                <Box
                  id="order-number-container"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  <Typography
                    sx={{
                      color:
                        order &&
                        (adjustTag(order) === 'FUTURE' ||
                          adjustTag(order) === 'UP NEXT' ||
                          adjustTag(order) === 'NO HUB' ||
                          adjustTag(order) === 'ASSIGNED' ||
                          adjustTag(order) === 'PLACED')
                          ? 'white'
                          : 'var(--Gray-gray12, #171717)',
                      leadingTrim: 'both',
                      textEdge: 'cap',
                      fontSize: '18px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: 'normal',
                    }}
                  >
                    {`Order ${order.id}`}
                  </Typography>
                </Box>
                <Chip
                  label={
                    <Typography
                      sx={{
                        color:
                          order &&
                          (adjustTag(order) === 'FUTURE' ||
                            adjustTag(order) === 'UP NEXT' ||
                            adjustTag(order) === 'NO HUB' ||
                            adjustTag(order) === 'ASSIGNED' ||
                            adjustTag(order) === 'PLACED')
                            ? 'black'
                            : 'var(--Overlay-White-white12, rgba(255, 255, 255, 0.92))',
                        leadingTrim: 'both',
                        textEdge: 'cap',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        lineHeight: 'normal',
                      }}
                    >
                      {formatStatus(order && adjustTag(order))}
                    </Typography>
                  }
                  sx={{
                    display: 'flex',
                    padding: '8px 12px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px',
                    borderRadius: '20px',
                    background:
                      order &&
                      (adjustTag(order) === 'CONFIRMED' ||
                        adjustTag(order) === 'PICKED UP' ||
                        adjustTag(order) === 'APPROVED')
                        ? '#1C124D'
                        : order &&
                          (adjustTag(order) === 'FUTURE' ||
                            adjustTag(order) === 'UP NEXT' ||
                            adjustTag(order) === 'NO HUB' ||
                            adjustTag(order) === 'ASSIGNED' ||
                            adjustTag(order) === 'PLACED')
                        ? '#FDFDFD'
                        : order && (adjustTag(order) === 'DELIVERY FAILED' || adjustTag(order) === 'CANCELED')
                        ? 'var(--_Tamagui-Red-red11, #CD2B31);'
                        : 'var(--secondary-mint-accessilbe, #1D7E6B)',
                  }}
                />
              </Box>
              <ArrowForwardIcon
                style={{
                  width: 'var(--1-5, 24px)',
                  height: 'var(--1-5, 24px)',
                  cursor: 'pointer',
                  color:
                    order &&
                    (adjustTag(order) === 'FUTURE' ||
                      adjustTag(order) === 'UP NEXT' ||
                      adjustTag(order) === 'NO HUB' ||
                      adjustTag(order) === 'ASSIGNED' ||
                      adjustTag(order) === 'PLACED')
                      ? 'white'
                      : 'inherit',
                }}
                onClick={handleNavigateToOrderDetails}
              />
            </Box>
          )}
          <Box
            id="order-details-content"
            sx={{
              display: 'flex',
              padding: '0px var(--3-5, 16px)',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--5, 24px)',
              alignSelf: 'stretch',
            }}
          >
            {!showFull && (order.dropoff_time != null || order.cutoff_time != null || order.placed_at != null) && (
              <Box
                id="delivery-details"
                sx={{
                  display: 'flex',
                  padding: '0px var(--1-5, 4px)',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 'var(--3-5, 16px)',
                  alignSelf: 'stretch',
                  borderRadius: '8px',
                }}
              >
                {getHeader(order)}
              </Box>
            )}

            <Box
              id="box-menu"
              sx={{
                display: 'flex',
                padding: '0px 8px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '12px',
                alignSelf: 'stretch',
                borderRadius: '8px',
                paddingTop: showFull ? '16px' : '0px',
              }}
            >
              <Box
                id="order-summary"
                sx={{
                  display: 'flex',
                  height: '36px',
                  alignItems: 'center',
                  gap: '8px',
                  alignSelf: 'stretch',
                }}
              >
                <Typography
                  sx={{
                    flex: '1 0 0',
                    color: 'var(--Gray-gray12, #171717)',
                    fontSize: '20px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '125%', // 25px
                  }}
                >
                  {showNumberOfItems ? 'Order Items' : 'Order Servings'}
                </Typography>
                {showNumberOfItems ? (
                  <Typography
                    sx={{
                      color: 'var(--Gray-gray12, #171717)',
                      textAlign: 'right',
                      leadingTrim: 'both',
                      textEdge: 'cap',
                      fontSize: '20px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: 'normal',
                    }}
                  >
                    {numberOfItems()}
                  </Typography>
                ) : ordersMetrics && ordersMetrics[order.id]?.totalServingsNeeded !== undefined ? (
                  <Typography
                    sx={{
                      color: 'var(--Gray-gray12, #171717)',
                      textAlign: 'right',
                      leadingTrim: 'both',
                      textEdge: 'cap',
                      fontSize: '20px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: 'normal',
                    }}
                  >
                    {ordersMetrics[order.id]?.totalServingsNeeded}
                  </Typography>
                ) : (
                  <CircularProgress size={24} />
                )}
              </Box>
              <Box
                id="items-list"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 'var(--3-5, 16px)',
                  alignSelf: 'stretch',
                }}
              >
                {currentMeals
                  .filter((meal) => meal.quantity > 0)
                  .slice(0, showFull ? currentMeals.length : showAllMeals ? currentMeals.length : 3)
                  .map((meal, index) => (
                    <Box
                      key={meal.box_id}
                      id="meal-item"
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        alignSelf: 'stretch',
                        borderRadius: '8px',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleMealClick(meal)}
                    >
                      <Box
                        sx={{
                          width: '64px',
                          height: '64px',
                          borderRadius: '64px',
                          background: `url(${meal.box_image}) lightgray 50% / cover no-repeat`,
                          boxShadow:
                            '1px 2px 3px 0px rgba(0, 0, 0, 0.17), 0px 1px 10px 0px rgba(0, 0, 0, 0.02), 0px 2px 4px -1px rgba(17, 17, 17, 0.03)',
                        }}
                      />

                      <Box
                        id="meal-name-container"
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'flex-start',
                          gap: '8px',
                          flex: '1 0 0',
                          alignSelf: 'stretch',
                        }}
                      >
                        <Box
                          id="meal-name"
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            alignSelf: 'stretch',
                          }}
                        >
                          <Typography
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              flex: '1 0 0',
                              alignSelf: 'stretch',
                              overflow: 'hidden',
                              color: 'var(--Gray-gray12, #171717)',
                              leadingTrim: 'both',
                              textEdge: 'cap',
                              textOverflow: 'ellipsis',
                              fontSize: '16px',
                              fontStyle: 'normal',
                              fontWeight: 400,
                              lineHeight: 'normal',
                              maxWidth: 'calc(100% - 50px)',
                              whiteSpace: 'normal',
                            }}
                          >
                            {meal.box_name}
                          </Typography>
                          <Box
                            id="meal-quantity"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <Typography
                              sx={{
                                color: 'var(--Gray-gray12, #171717)',
                                leadingTrim: 'both',
                                textEdge: 'cap',
                                fontSize: '16px',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: 'normal',
                              }}
                            >
                              {`x${meal.quantity}`}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                {!showFull && currentMeals.length > 3 && (
                  <Button
                    id="view-more-button"
                    onClick={handleViewMoreClick}
                    sx={{
                      display: 'flex',
                      // width: '179px',
                      width: '185px',
                      height: 'var(--4, 44px)',
                      padding: 'var(--0, 0px) var(--4, 18px)',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 'var(--4, 18px)',
                      borderRadius: 'var(--4, 9px)',
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'var(--color12, #1C124D)',
                        textAlign: 'center',
                        leadingTrim: 'both',
                        textEdge: 'cap',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        lineHeight: 'normal',
                      }}
                    >
                      {showAllMeals ? 'View Less' : 'View More'}
                    </Typography>
                    {showAllMeals ? (
                      <ExpandLessIcon
                        style={{
                          width: 'var(--1-5, 24px)',
                          height: '24px',
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <ExpandMoreIcon
                        style={{
                          width: 'var(--1-5, 24px)',
                          height: '24px',
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Button>
                )}
              </Box>
            </Box>
            {showFull && (adjustTag(order) === 'FUTURE' || adjustTag(order) === 'PREPARING') && (
              <Tooltip
                title={
                  showFull && ordersMetrics && ordersMetrics[order.id]?.totalServingsNeeded === undefined
                    ? "Sorry, you can't edit this order right now. Please reach out to our support team for assistance."
                    : ''
                }
                disableHoverListener={
                  !(showFull && ordersMetrics && ordersMetrics[order.id]?.totalServingsNeeded === undefined)
                }
                placement="top"
              >
                <div>
                  <Button
                    id="view-order-details-button"
                    onClick={showFull ? handleShowOrderDetails : handleNavigateToOrderDetails}
                    disabled={showFull && ordersMetrics && ordersMetrics[order.id]?.totalServingsNeeded === undefined}
                    sx={{
                      display: 'flex',
                      width: '245px',
                      height: 'var(--3, 36px)',
                      padding: 'var(--0, 0px) var(--3, 13px)',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 'var(--3, 13px)',
                      borderRadius: 'var(--3, 7px)',
                      border: 'var(--1, 2px) solid var(--color12, #1C124D)',
                      background: 'var(--color1, #FDFDFD)',
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'var(--color12, #1C124D)',
                        textAlign: 'center',
                        leadingTrim: 'both',
                        textEdge: 'cap',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        lineHeight: 'normal',
                      }}
                    >
                      {showFull ? 'Edit Items' : 'View Order Details'}
                    </Typography>
                    <ArrowForwardIcon
                      style={{
                        width: 'var(--1, 20px)',
                        height: 'var(--1, 20px)',
                        flexShrink: 0,
                      }}
                    />
                  </Button>
                </div>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Drawer
          anchor="right"
          open={isAdjustmentCardOpen}
          onClose={handleCloseAdjustmentCard}
          PaperProps={{
            sx: {
              width: '400px',
              borderRadius: 2,
              p: 2,
              backgroundColor: darkMode ? '#333' : '#fff',
              color: darkMode ? '#fff' : '#000',
            },
          }}
        >
          <OrderDetailsAdjustmentCard
            order={{ ...order, meals: currentMeals }}
            onClose={handleCloseAdjustmentCard}
          />
        </Drawer>
        {selectedMeal && (
          <Dialog
            open={true}
            onClose={closeMealInfoCard}
            maxWidth="md"
          >
            <MealInfoCard
              mealInfo={selectedMeal}
              order={order}
              meal={selectedMeal}
              onClose={closeMealInfoCard}
            />
          </Dialog>
        )}
        {isReportIssueOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                toggleReportIssueCard();
              }
            }}
          >
            <ReportOrderIssueCard
              onClose={toggleReportIssueCard}
              orderID={order.id}
            />
          </div>
        )}
      </div>
    );
  },
);

// Add a display name to help with debugging
OrderDetails.displayName = 'OrderDetails';

export default OrderDetails;
